import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const res = await api.get(`/payments/result/${sessionId}`);
          clearCart();
          setState("paid");
          setTimeout(() => navigate(`/order/${res.data.order_id}`, { state: { order: res.data.order } }), 900);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current < 25) timer = setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate, clearCart]);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-[#00F0FF]" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white" data-testid="payment-checking">Confirming your payment…</h1>
          <p className="mt-2 text-slate-400">Hang tight while we verify your transaction with Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-[#00E676]" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white" data-testid="payment-paid">Payment successful!</h1>
          <p className="mt-2 text-slate-400">Redirecting to your order confirmation…</p>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="h-14 w-14 text-[#FF2A6D]" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white" data-testid="payment-failed">We couldn't confirm your payment</h1>
          <p className="mt-2 text-slate-400">If you were charged, contact support@dropworldwide.com with your details.</p>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => navigate("/checkout")} className="rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="payment-retry-btn">Back to Checkout</Button>
            <Button onClick={() => navigate("/shop")} variant="outline" className="rounded-xl border-white/15 bg-transparent text-slate-100 hover:bg-white/5">Continue Shopping</Button>
          </div>
        </>
      )}
    </div>
  );
}
